/**
 * GlossaryPage Component - GOSSA v2.0
 * Context-aware glossary view for participants
 * Automatically displays definitions based on user's selected_track
 * 
 * SOURCE PROTECTION: No source references (primary_source, references) are displayed.
 * These fields are internal only and never exposed to end users.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import {
  Search, BookOpen, Filter, ChevronRight, Loader2, Globe, Languages, Tag
} from 'lucide-react';

const API = (window.location.origin || '') + '/api';

// Standalone Glossary Page
const GlossaryPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [terms, setTerms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLetter, setSelectedLetter] = useState(''); // Alphabet filter
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [userTrack, setUserTrack] = useState(null);

  // Alphabet for filter
  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API}/glossary/categories`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, [getAuthHeaders]);

  // Fetch terms with context-aware display
  const fetchTerms = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/glossary/terms?limit=200`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      
      const res = await fetch(url, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setTerms(data.terms || []);
        // Backend provides user_track in response
        if (data.user_track) {
          setUserTrack(data.user_track);
        }
      }
    } catch (error) {
      console.error('Failed to fetch terms:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selectedCategory, searchTerm]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const debounce = setTimeout(fetchTerms, 300);
    return () => clearTimeout(debounce);
  }, [fetchTerms]);

  // Filter terms by selected letter (client-side for instant feedback)
  const filteredTerms = selectedLetter 
    ? terms.filter(t => (t.term || '')[0]?.toUpperCase() === selectedLetter)
    : terms;

  // Get available letters (letters that have at least one term)
  const availableLetters = new Set(
    terms.map(t => (t.term || '')[0]?.toUpperCase()).filter(Boolean)
  );

  // Group terms by first letter
  const groupedTerms = filteredTerms.reduce((acc, term) => {
    const letter = (term.term || '')[0]?.toUpperCase() || '#';
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(term);
    return acc;
  }, {});

  // Get the display definition based on user's track
  const getDisplayDefinition = (term) => {
    // If backend provides pre-computed display info, use it
    if (term.display) {
      return {
        definition: term.display.definition,
        source: term.display.source,
        isContextSpecific: term.display.is_context_specific,
        contextUsed: term.display.context_used
      };
    }
    
    // Fallback: compute on frontend
    const contextDefs = term.context_definitions || {};
    if (userTrack && contextDefs[userTrack]) {
      return {
        definition: contextDefs[userTrack].definition,
        source: contextDefs[userTrack].reference,
        isContextSpecific: true,
        contextUsed: userTrack
      };
    }
    
    return {
      definition: term.master_definition || term.definition,
      source: term.primary_source || term.reference,
      isContextSpecific: false,
      contextUsed: null
    };
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BookOpen className="w-8 h-8 text-teal-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-white">GOSSA Glossar</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Fachbegriffe und Definitionen für Ihre Prüfungsvorbereitung
              </p>
            </div>
          </div>
          {userTrack && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Globe className="w-3 h-3" />
              Kontext: {userTrack}
            </Badge>
          )}
        </div>

        {/* Search & Filter */}
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Begriff, Definition oder Tags suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="glossary-search"
                />
              </div>
              <Select value={selectedCategory || "all"} onValueChange={(val) => setSelectedCategory(val === "all" ? "" : val)}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Alle Kategorien" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Kategorien</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Alphabet Filter Bar */}
        <div className="flex flex-wrap justify-center gap-1 py-2" data-testid="alphabet-filter">
          <button
            onClick={() => setSelectedLetter('')}
            className={`w-8 h-8 rounded text-sm font-medium transition-all ${
              selectedLetter === '' 
                ? 'bg-teal-600 text-white' 
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-teal-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
            }`}
            data-testid="alphabet-all"
          >
            Alle
          </button>
          {ALPHABET.map(letter => {
            const hasTerms = availableLetters.has(letter);
            return (
              <button
                key={letter}
                onClick={() => hasTerms && setSelectedLetter(selectedLetter === letter ? '' : letter)}
                disabled={!hasTerms}
                className={`w-8 h-8 rounded text-sm font-medium transition-all ${
                  selectedLetter === letter 
                    ? 'bg-teal-600 text-white' 
                    : hasTerms
                      ? 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-teal-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                      : 'bg-slate-100 dark:bg-slate-900 text-slate-300 dark:text-slate-600 cursor-not-allowed border border-slate-100 dark:border-slate-800'
                }`}
                data-testid={`alphabet-${letter}`}
              >
                {letter}
              </button>
            );
          })}
        </div>

        {/* Active Filters Info */}
        {(selectedLetter || selectedCategory) && (
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <span>Filter aktiv:</span>
            {selectedLetter && (
              <Badge variant="secondary" className="cursor-pointer" onClick={() => setSelectedLetter('')}>
                Buchstabe: {selectedLetter} ✕
              </Badge>
            )}
            {selectedCategory && (
              <Badge variant="secondary" className="cursor-pointer" onClick={() => setSelectedCategory('')}>
                Kategorie: {selectedCategory} ✕
              </Badge>
            )}
            <button 
              onClick={() => { setSelectedLetter(''); setSelectedCategory(''); }}
              className="text-teal-600 hover:underline ml-2"
            >
              Alle zurücksetzen
            </button>
          </div>
        )}

        {/* Terms List */}
        <Card>
          <CardContent className="p-0">
            <ScrollArea className="h-[600px]">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : filteredTerms.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Keine Begriffe gefunden</p>
                  <p className="text-sm mt-2">
                    {selectedLetter 
                      ? `Keine Begriffe mit "${selectedLetter}" gefunden.`
                      : 'Das Glossar wird derzeit aufgebaut.'
                    }
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {Object.keys(groupedTerms).sort().map(letter => (
                    <div key={letter} id={`letter-${letter}`}>
                      <div className="sticky top-0 bg-slate-100 dark:bg-slate-800 px-4 py-2 font-bold text-slate-600 dark:text-slate-300">
                        {letter}
                      </div>
                      {groupedTerms[letter].map(term => {
                        const displayInfo = getDisplayDefinition(term);
                        return (
                          <button
                            key={term.term_id}
                            onClick={() => setSelectedTerm(term)}
                            className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors flex items-center justify-between group"
                            data-testid={`term-${term.term_id}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-slate-800 dark:text-white">
                                  {term.term}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {term.category}
                                </Badge>
                                {displayInfo.isContextSpecific && (
                                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                                    <Globe className="w-3 h-3 mr-1" />
                                    {displayInfo.contextUsed}
                                  </Badge>
                                )}
                                {term.do_not_translate && (
                                  <Languages className="w-3 h-3 text-orange-500" title="Nicht übersetzen" />
                                )}
                              </div>
                              <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-1 mt-1">
                                {displayInfo.definition}
                              </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Term Detail Dialog */}
        <Dialog open={!!selectedTerm} onOpenChange={() => setSelectedTerm(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-teal-600" />
                {selectedTerm?.term}
              </DialogTitle>
            </DialogHeader>
            {selectedTerm && (
              <TermDetailView term={selectedTerm} userTrack={userTrack} />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Term Detail View Component
// SOURCE PROTECTION: No source references displayed to users
const TermDetailView = ({ term, userTrack }) => {
  const contextDefs = term.context_definitions || {};
  const hasContextDefs = Object.keys(contextDefs).length > 0;
  
  // Determine which definition to show first
  const primaryContext = userTrack && contextDefs[userTrack] ? userTrack : null;
  const primaryDefinition = primaryContext 
    ? contextDefs[primaryContext].definition 
    : (term.master_definition || term.definition);
  // SOURCE PROTECTION: primarySource removed - never displayed
  
  return (
    <div className="space-y-4">
      {/* Badges */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge>{term.category}</Badge>
        {primaryContext && (
          <Badge variant="secondary" className="bg-blue-100 text-blue-700">
            <Globe className="w-3 h-3 mr-1" />
            {primaryContext}-spezifisch
          </Badge>
        )}
        {term.do_not_translate && (
          <Badge variant="outline" className="text-orange-600 border-orange-300">
            <Languages className="w-3 h-3 mr-1" />
            Framework-Begriff
          </Badge>
        )}
        {term.version && (
          <Badge variant="outline" className="text-slate-500">
            v{term.version}
          </Badge>
        )}
      </div>
      
      {/* Primary Definition */}
      <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-800">
        {primaryContext && (
          <p className="text-xs text-teal-600 dark:text-teal-400 mb-2 font-medium">
            Definition für {primaryContext}:
          </p>
        )}
        <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
          {primaryDefinition}
        </p>
        {/* SOURCE PROTECTION: Source references are internal only - never displayed to users */}
      </div>
      
      {/* Master Definition (if showing context-specific) */}
      {primaryContext && term.master_definition && (
        <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border">
          <p className="text-xs text-slate-500 mb-1 font-medium">Master-Definition:</p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {term.master_definition}
          </p>
          {/* SOURCE PROTECTION: primary_source is internal only */}
        </div>
      )}
      
      {/* Other Context Definitions */}
      {hasContextDefs && Object.keys(contextDefs).filter(k => k !== primaryContext).length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-500 mb-2">Weitere Kontext-Definitionen:</p>
          <div className="space-y-2">
            {Object.entries(contextDefs)
              .filter(([ctx]) => ctx !== primaryContext)
              .map(([ctx, data]) => (
                <div key={ctx} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded border-l-4 border-l-slate-300">
                  <Badge variant="outline" className="mb-1 text-xs">{ctx}</Badge>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{data.definition}</p>
                  {/* SOURCE PROTECTION: context reference is internal only */}
                </div>
              ))}
          </div>
        </div>
      )}
      
      {/* Tags */}
      {term.tags?.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
            <Tag className="w-3 h-3" />
            Tags:
          </p>
          <div className="flex flex-wrap gap-1">
            {term.tags.map((tag, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      {/* SOURCE PROTECTION: References section completely removed - internal only */}
    </div>
  );
};

// Compact Glossary Widget for Training Mode
export const GlossaryWidget = ({ isOpen, onClose }) => {
  const { getAuthHeaders } = useAuth();
  const [terms, setTerms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [userTrack, setUserTrack] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    
    const fetchTerms = async () => {
      setLoading(true);
      try {
        let url = `${API}/glossary/terms?limit=100`;
        if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
        
        const res = await fetch(url, {
          headers: getAuthHeaders()
        });
        
        if (res.ok) {
          const data = await res.json();
          setTerms(data.terms || []);
          if (data.user_track) {
            setUserTrack(data.user_track);
          }
        }
      } catch (error) {
        console.error('Failed to fetch terms:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchTerms, 300);
    return () => clearTimeout(debounce);
  }, [isOpen, searchTerm, getAuthHeaders]);

  if (!isOpen) return null;

  // Get display definition
  const getDisplayDef = (term) => {
    if (term.display) return term.display.definition;
    const ctx = term.context_definitions || {};
    if (userTrack && ctx[userTrack]) return ctx[userTrack].definition;
    return term.master_definition || term.definition;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-teal-600" />
            Glossar
            {userTrack && (
              <Badge variant="outline" className="text-xs ml-2">
                <Globe className="w-3 h-3 mr-1" />
                {userTrack}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Begriff suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            autoFocus
          />
        </div>

        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : terms.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              {searchTerm ? 'Keine Ergebnisse' : 'Suchbegriff eingeben'}
            </div>
          ) : (
            <div className="space-y-3">
              {terms.map(term => (
                <div key={term.term_id} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-medium text-slate-800 dark:text-white">
                      {term.term}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {term.category}
                    </Badge>
                    {term.display?.is_context_specific && (
                      <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                        <Globe className="w-3 h-3 mr-1" />
                        {term.display.context_used}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    {getDisplayDef(term)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default GlossaryPage;
